import { useEffect, useState, useMemo } from 'react';
import { getNextShabbatDays, getNextRecurringDates, formatDate } from '@/utils/dateUtils';

/**
 * Custom hook for managing delivery dates
 * @param {object} restaurant - Restaurant object with delivery configuration
 * @param {object} cart - Cart object
 * @param {function} setCart - Function to update cart
 * @returns {{deliveryOptions: Array<string>, nextThursday: string, nextFriday: string}} Delivery date options
 */
export const useDeliveryDates = (restaurant, cart, setCart) => {
	const [nextThursday, setNextThursday] = useState('');
	const [nextFriday, setNextFriday] = useState('');

	useEffect(() => {
		const shabbatDays = getNextShabbatDays();
		setNextThursday(shabbatDays.thursday);
		setNextFriday(shabbatDays.friday);
	}, []);

	const deliveryOptions = useMemo(() => {
		if (!restaurant?.is_delivery_enabled) {
			return [];
		}

		if (restaurant?.is_shabbat_mode) {
			return [nextThursday, nextFriday].filter(Boolean);
		}

		if (restaurant?.is_recurring_mode && restaurant?.recurring_days?.length > 0) {
			return getNextRecurringDates(restaurant.recurring_days);
		}

		if (restaurant?.delivery_dates && restaurant.delivery_dates.length > 0) {
			return restaurant.delivery_dates.map(date => formatDate(new Date(date)));
		}

		return [];
	}, [restaurant, nextThursday, nextFriday]);

	useEffect(() => {
		if (deliveryOptions.length === 1 && cart.delivery_date !== deliveryOptions[0]) {
			setCart((prev) => ({
				...prev,
				delivery_date: deliveryOptions[0],
			}));
		}
	}, [deliveryOptions, cart.delivery_date, setCart]);

	return { deliveryOptions, nextThursday, nextFriday };
};

